import React, { useState, useEffect } from 'react';
import '../styles/Input.css';

export type Validator = (value: any) => string | null;

export interface InputProps {
  // Value
  value: string | number;
  onChange: (value: string | number) => void;

  // Input attributes
  type?: 'text' | 'number' | 'email' | 'password' | 'url';
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;

  // Validation
  error?: string | null;
  validators?: Validator[];

  // Number constraints (when type="number")
  min?: number;
  max?: number;
  step?: number;

  // Styling
  className?: string;
  style?: React.CSSProperties;

  // Accessibility
  id?: string;
  label?: string;
  'aria-label'?: string;
}

export const Input: React.FC<InputProps> = ({
  value,
  onChange,
  type = 'text',
  placeholder = '',
  disabled = false,
  required = false,
  autoFocus = false,
  error: externalError,
  validators = [],
  min,
  max,
  step,
  className = '',
  style,
  id,
  label,
  'aria-label': ariaLabel,
}) => {
  const [internalError, setInternalError] = useState<string | null>(null);

  // Use external error if provided, otherwise use internal validation error
  const errorMessage = externalError ?? internalError;

  // Run validators on value change
  useEffect(() => {
    if (validators.length === 0) return;

    for (const validator of validators) {
      const validationError = validator(value);
      if (validationError) {
        setInternalError(validationError);
        return;
      }
    }
    setInternalError(null);
  }, [value, validators]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
    onChange(newValue);
  };

  const inputId = id || (label ? `input-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

  return (
    <div className="input-wrapper" style={style}>
      {label && (
        <label htmlFor={inputId} className="input-label">
          {label}
          {required && <span className="required-indicator">*</span>}
        </label>
      )}
      <input
        id={inputId}
        type={type}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        min={min}
        max={max}
        step={step}
        aria-label={ariaLabel || label}
        aria-invalid={!!errorMessage}
        aria-describedby={errorMessage ? `${inputId}-error` : undefined}
        className={`input ${className} ${errorMessage ? 'input-error' : ''}`}
      />
      {errorMessage && (
        <div id={`${inputId}-error`} className="error-message" role="alert">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

// Common validators
export const validators = {
  required: (value: any): string | null => {
    if (value === '' || value === null || value === undefined) {
      return 'This field is required';
    }
    return null;
  },

  minNumber: (min: number) => (value: number): string | null => {
    if (typeof value !== 'number' || isNaN(value)) {
      return 'Please enter a valid number';
    }
    return value < min ? `Must be at least ${min}` : null;
  },

  maxNumber: (max: number) => (value: number): string | null => {
    if (typeof value !== 'number' || isNaN(value)) {
      return 'Please enter a valid number';
    }
    return value > max ? `Must be at most ${max}` : null;
  },

  minLength: (min: number) => (value: string): string | null => {
    return value.length < min ? `Must be at least ${min} characters` : null;
  },

  maxLength: (max: number) => (value: string): string | null => {
    return value.length > max ? `Must be at most ${max} characters` : null;
  },

  email: (value: string): string | null => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value) ? null : 'Please enter a valid email address';
  },

  xrplAddress: (value: string): string | null => {
    const xrplRegex = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;
    return xrplRegex.test(value) ? null : 'Please enter a valid XRPL address';
  },

  url: (value: string): string | null => {
    try {
      new URL(value);
      return null;
    } catch {
      return 'Please enter a valid URL';
    }
  },
};
